import { Col, Glyphicon } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import GenericFormField from 'components/forms/genericFormField/GenericFormField';
import TextField from 'components/forms/textField/TextField';

interface TierBase {
  refundPercentage: number;
  refundThreshold: number;
}

interface Props<TTier extends TierBase> {
  name: string;
  getEmptyTier: (currentTiers: TTier[]) => TTier;
  getTierKey: (tier: TTier, index: number) => string;
  maxTiers?: number;
}

// Using `setValue` instead of `useFieldArray` to avoid marking all fields as
// dirty. See history in `PropertyVrboSettingsPolicyPeriodsField` and:
// https://github.com/react-hook-form/react-hook-form/issues/12784
// https://github.com/react-hook-form/react-hook-form/issues/11929
const VrboCustomTiersField = <TTier extends TierBase>({
  name,
  getEmptyTier,
  getTierKey,
  maxTiers = 3,
}: Props<TTier>) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const tiers = (useWatch({ name }) as TTier[] | undefined) ?? [];

  const onAdd = () => {
    setValue(name, [...tiers, getEmptyTier(tiers)], { shouldDirty: true });
  };

  const onDelete = (index: number) => {
    setValue(
      name,
      tiers.filter((_, i) => i !== index),
      { shouldDirty: true },
    );
  };

  return (
    <>
      {tiers.map(
        (tier, index) =>
          tier && (
            <GenericFormField
              key={getTierKey(tier, index)}
              containerClassName="policy-period-field"
              label={t(
                'pageProperty.mainSettings.vrboSettings.policyPeriods.label',
                { value: index + 1 },
              )}
              name={`${name}.${index}`}
              colSmInput={7}
              colSmLabel={5}
              fieldRenderer={() => (
                <div className="policy-period">
                  <TextField
                    label=""
                    name={`${name}.${index}.refundPercentage`}
                    rightAddons={['%']}
                    colSmInput={12}
                    type="number"
                  />
                  <span className="policy-period-label">
                    {t(
                      'pageProperty.mainSettings.vrboSettings.policyPeriods.refundUpTo',
                    )}
                  </span>
                  <TextField
                    label=""
                    name={`${name}.${index}.refundThreshold`}
                    rightAddons={[t('common.days')]}
                    colSmInput={12}
                    type="number"
                  />
                  <span className="policy-period-label">
                    {t(
                      'pageProperty.mainSettings.vrboSettings.policyPeriods.beforeCheckIn',
                    )}
                  </span>
                  {index !== 0 && (
                    <Button
                      bsStyle="link"
                      data-testid="delete-custom-policy-button"
                      onClick={() => onDelete(index)}
                    >
                      <Glyphicon glyph="remove-sign" />
                    </Button>
                  )}
                </div>
              )}
            />
          ),
      )}

      {tiers.length < maxTiers && (
        <Col className="add-policy-period-column" xsOffset={4} smOffset={5}>
          <Button
            bsStyle="link"
            data-testid="vrbo-add-policy-period-button"
            onClick={onAdd}
          >
            {t(
              'pageProperty.mainSettings.vrboSettings.policyPeriods.addPolicyPeriod',
            )}
          </Button>
        </Col>
      )}
    </>
  );
};

export default VrboCustomTiersField;
